import React, {
    useRef,
    useState
} from "react"
import {
    Box,
    Typography,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Radio,
    RadioGroup,
    FormControlLabel,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    CircularProgress
} from "@mui/material"
import {
    CloseOutlined,
    UploadFileOutlined,
    CheckCircleOutline,
    ErrorOutline,
    WarningAmberOutlined
} from "@mui/icons-material"
import { Message, toaster } from "rsuite"
import _ from "lodash"
import { useImportDatabaseDataMutation } from "../Redux/actions/setting.action"

const number = new Intl.NumberFormat("en-IN")

const MODES = [
    {
        value: "insert",
        label: "Add new rows only",
        description: "Stops if a row's id already exists in the table"
    },
    {
        value: "skip",
        label: "Skip rows that already exist",
        description: "Adds new rows and leaves existing ones untouched"
    },
    {
        value: "upsert",
        label: "Update rows that already exist",
        description: "Adds new rows and overwrites existing ones with the file's values"
    }
]

const errorMessage = (error, fallback) => {
    const detail = _.get(error, "data.detail")
    return _.isString(detail) ? detail : fallback
}

const notify = (type, message) =>
    toaster.push(
        <Message showIcon type={type}>{message}</Message>,
        { placement: "topCenter" }
    )

const formatSize = (bytes) =>
    bytes < 1024 * 1024 ? `${Math.max(1, Math.round(bytes / 1024))} KB` : `${(bytes / (1024 * 1024)).toFixed(1)} MB`

const primaryButtonSx = {
    textTransform: "none",
    fontWeight: 700,
    borderRadius: "10px",
    px: 2.5,
    bgcolor: "var(--color-primary)",
    boxShadow: "none",
    "&:hover": { bgcolor: "var(--color-primary-hover)", boxShadow: "none" }
}

function CheckResult({ result }) {
    const totals = _.reduce(
        result.tables,
        (sum, table) => ({
            inserted: sum.inserted + table.inserted,
            updated: sum.updated + table.updated,
            skipped: sum.skipped + table.skipped
        }),
        { inserted: 0, updated: 0, skipped: 0 }
    )
    const hiddenErrors = result.error_count - result.errors.length

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Box sx={{
                display: "flex",
                gap: 1.5,
                p: 1.5,
                borderRadius: "12px",
                bgcolor: result.ok ? "#ecfdf5" : "var(--color-danger-light)",
                color: result.ok ? "#047857" : "#991b1b"
            }}>
                {result.ok
                    ? <CheckCircleOutline fontSize="small" sx={{ mt: "2px" }} />
                    : <ErrorOutline fontSize="small" sx={{ mt: "2px" }} />}
                <Box>
                    <Typography sx={{ fontSize: 14, fontWeight: 700 }}>{result.message}</Typography>
                    {result.ok && (
                        <Typography sx={{ fontSize: 13 }}>
                            {number.format(totals.inserted)} to add · {number.format(totals.updated)} to update · {number.format(totals.skipped)} to skip
                        </Typography>
                    )}
                </Box>
            </Box>

            {result.ignored.length > 0 && (
                <Box sx={{ display: "flex", gap: 1, color: "#b45309", fontSize: 13 }}>
                    <WarningAmberOutlined fontSize="small" />
                    <Typography sx={{ fontSize: 13 }}>
                        Not a table in the database, so ignored: {result.ignored.join(", ")}
                    </Typography>
                </Box>
            )}

            {result.errors.length > 0 && (
                <Box sx={{ border: "1px solid var(--border-color)", borderRadius: "12px", overflow: "auto", maxHeight: 260 }}>
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow sx={{ "& th": { fontSize: 12, fontWeight: 700, bgcolor: "#f8fafc" } }}>
                                <TableCell>Table</TableCell>
                                <TableCell>Row</TableCell>
                                <TableCell>Column</TableCell>
                                <TableCell>Problem</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {result.errors.map((error, index) => (
                                <TableRow key={index} sx={{ "& td": { fontSize: 13 } }}>
                                    <TableCell>{error.table || "—"}</TableCell>
                                    <TableCell>{error.row || "—"}</TableCell>
                                    <TableCell>{error.column || "—"}</TableCell>
                                    <TableCell>{error.message}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    {hiddenErrors > 0 && (
                        <Typography sx={{ p: 1.5, fontSize: 12, color: "var(--text-secondary)" }}>
                            …and {number.format(hiddenErrors)} more
                        </Typography>
                    )}
                </Box>
            )}

            {result.ok && result.tables.length > 1 && (
                <Box sx={{ border: "1px solid var(--border-color)", borderRadius: "12px", overflow: "auto", maxHeight: 260 }}>
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow sx={{ "& th": { fontSize: 12, fontWeight: 700, bgcolor: "#f8fafc" } }}>
                                <TableCell>Table</TableCell>
                                <TableCell align="right">Add</TableCell>
                                <TableCell align="right">Update</TableCell>
                                <TableCell align="right">Skip</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {result.tables.map((table) => (
                                <TableRow key={table.name} sx={{ "& td": { fontSize: 13, fontVariantNumeric: "tabular-nums" } }}>
                                    <TableCell>{table.name}</TableCell>
                                    <TableCell align="right">{number.format(table.inserted)}</TableCell>
                                    <TableCell align="right">{number.format(table.updated)}</TableCell>
                                    <TableCell align="right">{number.format(table.skipped)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Box>
            )}
        </Box>
    )
}

/**
 * Imports a file produced by the Database page's download. With tableName it
 * takes that table's CSV or Excel file; without, a whole-database Excel or ZIP.
 * The file is always checked first and only saved after the owner confirms.
 */
export default function DatabaseImportDialog({ tableName, onClose }) {
    const inputRef = useRef(null)
    const [file, setFile] = useState(null)
    const [mode, setMode] = useState("insert")
    const [checked, setChecked] = useState(null)
    const [importData, importState] = useImportDatabaseDataMutation()
    const busy = importState.isLoading

    const accept = tableName ? ".csv,.xlsx" : ".xlsx,.zip"

    const resetCheck = () => {
        setChecked(null)
        importState.reset()
    }

    const run = async (dryRun) => {
        try {
            const result = await importData({ table_name: tableName, file, mode, dry_run: dryRun }).unwrap()
            if (dryRun || !result.ok) {
                setChecked(result)
                return
            }
            notify("success", result.message)
            onClose()
        } catch (error) {
            notify("error", errorMessage(error, "Could not read the file. Please try again."))
        }
    }

    const changes = checked && checked.ok
        ? _.sumBy(checked.tables, (table) => table.inserted + table.updated)
        : 0

    return (
        <Dialog
            open
            onClose={busy ? undefined : onClose}
            fullWidth
            maxWidth="sm"
            PaperProps={{ sx: { borderRadius: "18px" } }}
        >
            <DialogTitle sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2, pb: 1 }}>
                <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontFamily: "var(--font-heading)", fontWeight: 700, fontSize: 20 }}>
                        {tableName ? "Import rows" : "Import tables"}
                    </Typography>
                    <Typography noWrap sx={{ fontSize: 13, color: "var(--text-secondary)" }}>
                        {tableName
                            ? <>Into <strong>{tableName}</strong> from a CSV or Excel file</>
                            : "From a whole-database Excel workbook or ZIP of CSVs"}
                    </Typography>
                </Box>
                <IconButton onClick={onClose} disabled={busy} aria-label="Close">
                    <CloseOutlined fontSize="small" />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ pt: "8px !important", display: "flex", flexDirection: "column", gap: 2.5 }}>
                <Typography sx={{ fontSize: 13, color: "var(--text-secondary)" }}>
                    Use a file in the same format as the download: the first row holds the column
                    names. Leave the id blank to let the database assign one.
                </Typography>

                <input
                    ref={inputRef}
                    type="file"
                    accept={accept}
                    hidden
                    onChange={(e) => {
                        setFile(e.target.files[0] || null)
                        resetCheck()
                        e.target.value = ""
                    }}
                />
                <Box
                    component="button"
                    type="button"
                    onClick={() => inputRef.current.click()}
                    disabled={busy}
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        p: 2,
                        border: "1.5px dashed var(--border-color)",
                        borderRadius: "12px",
                        bgcolor: "#f8fafc",
                        cursor: "pointer",
                        textAlign: "left",
                        font: "inherit",
                        "&:hover": { borderColor: "var(--color-primary)" }
                    }}
                >
                    <UploadFileOutlined sx={{ color: "var(--color-primary)" }} />
                    <Box sx={{ minWidth: 0 }}>
                        <Typography noWrap sx={{ fontSize: 14, fontWeight: 600 }}>
                            {file ? file.name : "Choose a file"}
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: "var(--text-secondary)" }}>
                            {file ? formatSize(file.size) : `${accept.replaceAll(",", " or ")}, up to 25 MB`}
                        </Typography>
                    </Box>
                </Box>

                <Box>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 0.5 }}>
                        When a row's id is already in the table
                    </Typography>
                    <RadioGroup
                        value={mode}
                        onChange={(e) => {
                            setMode(e.target.value)
                            resetCheck()
                        }}
                    >
                        {MODES.map((option) => (
                            <FormControlLabel
                                key={option.value}
                                value={option.value}
                                disabled={busy}
                                control={<Radio size="small" />}
                                sx={{ alignItems: "flex-start", "& .MuiRadio-root": { pt: 0.5 } }}
                                label={
                                    <Box>
                                        <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{option.label}</Typography>
                                        <Typography sx={{ fontSize: 12, color: "var(--text-secondary)" }}>
                                            {option.description}
                                        </Typography>
                                    </Box>
                                }
                            />
                        ))}
                    </RadioGroup>
                </Box>

                {checked && <CheckResult result={checked} />}
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                <Button
                    onClick={onClose}
                    disabled={busy}
                    sx={{ textTransform: "none", color: "var(--text-secondary)", fontWeight: 600 }}
                >
                    Cancel
                </Button>
                {checked && checked.ok && checked.dry_run ? (
                    <Button
                        variant="contained"
                        onClick={() => run(false)}
                        disabled={busy || changes === 0}
                        startIcon={busy ? <CircularProgress size={16} color="inherit" /> : null}
                        sx={primaryButtonSx}
                    >
                        {busy
                            ? "Importing…"
                            : changes === 0
                                ? "Nothing to import"
                                : `Import ${number.format(changes)} row${changes === 1 ? "" : "s"}`}
                    </Button>
                ) : (
                    <Button
                        variant="contained"
                        onClick={() => run(true)}
                        disabled={busy || !file}
                        startIcon={busy ? <CircularProgress size={16} color="inherit" /> : null}
                        sx={primaryButtonSx}
                    >
                        {busy ? "Checking…" : checked ? "Check again" : "Check file"}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    )
}
